import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GenericService, LoaderConfig } from './generic.service';
import { BehaviorSubject, catchError, concatMap, EMPTY, map, Observable, Subject, tap } from 'rxjs';
import { ServerResponse } from '../models/ServerResponse';
import { ClientPortalUser, CreateClient, IClientPortalUser } from '../models/Client';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { UserProfile, UserProfileEdit } from '../models/UserProfile';
import { AuthService } from './auth.service';
import { EntitiesService } from './entities.service';
import { EntityExistenceAndPermissions } from '../models/Entity';
import { User } from 'oidc-client';
import { SnackBarType } from '../models/Snackbar';
import { RequestCacheConfig } from '../models/Request';
import { pt, es, enGB } from 'date-fns/locale';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  protected userDataSubject: BehaviorSubject<ClientPortalUser | null> = new BehaviorSubject<ClientPortalUser | null>(null);
  userData$: Observable<ClientPortalUser | null> = this.userDataSubject.asObservable();

  get userCountryCode(): string {
    return this.userDataSubject.value!.countryCode.toLocaleLowerCase();
  }

  get userCountryLocale(): Locale {
    switch( this.userCountryCode ) {
      case 'pt': return pt; break;
      case 'es': return es; break;
      default: return enGB; break;
    }
  }

  get userData(): ClientPortalUser | null {
    return this.userDataSubject.value;
  }

  set userData(user: ClientPortalUser | null) {
    this.userDataSubject.next(user);

    if (user) {
      localStorage.setItem('ClientPortalUser', JSON.stringify(user));
      localStorage.setItem('selectedSIGA', user.countryCode.toLowerCase());
    } else {
      localStorage.removeItem('ClientPortalUser')
      localStorage.removeItem('selectedSIGA')
    }
    
  }

  constructor(private _httpService: GenericService, private _authService: AuthService ) {
  }

  /** Checks that the client data has every field the authenticated pages rely on */
  static hasRequiredClientData(user: Partial<ClientPortalUser> | null | undefined): boolean {
    return !!user
      && !String.isNullOrUndefinedOrWhitespace(user.email)
      && !String.isNullOrUndefinedOrWhitespace(user.identityServerId)
      && user.clientPortalId != null
      && !String.isNullOrUndefinedOrWhitespace(user.countryCode)
      && !String.isNullOrUndefinedOrWhitespace(user.countryId);
  }

  /** Reads the client data saved at login, returning null if it is missing or incomplete */
  static getStoredClientData(): ClientPortalUser | null {
    try {
      const stored = localStorage.getItem('ClientPortalUser');
      const user = stored ? JSON.parse(stored) : null;
      return UsersService.hasRequiredClientData(user) ? user : null;
    } catch {
      return null;
    }
  }

  forgotPassword(email: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    let headers = new HttpHeaders().set('InterceptorSkipHeader', '');
    
    return this._httpService.post(environment.apiUrl + 'ClientPortalUsers/' + email + '/PasswordResetRequest', undefined, headers, loaderConfig);
  }

  resetUserPassword(email: string, body: any, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    return this._httpService.post(environment.apiUrl + 'MVIdentityUsers/' + email + '/PasswordReset', body, undefined, loaderConfig);
  }

  confirmUserEmail(email: string, body: any, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    return this._httpService.post(environment.apiUrl + 'MVIdentityUsers/' + email + '/ConfirmEmail', body, undefined, loaderConfig)
  }

  createUser(body: CreateClient, country?: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    return this._httpService.post(environment.apiUrl + 'ClientPortalUsers/' + (country ?? this.userDataSubject.value!.countryCode), body, undefined, loaderConfig);
  }

  getCurrentUserClaims(userEmail: string, loaderConfig: LoaderConfig | boolean = false, cacheConfig: RequestCacheConfig): Observable<ServerResponse<any>> {
    return this._httpService.get<any>(environment.apiUrl + 'MVApiConsumerUsers/' + userEmail, undefined, loaderConfig, cacheConfig);
  }

  getCurrentUser(country: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<UserProfile>> {
    return this._httpService.get<UserProfile>(environment.apiUrl + 'ClientPortalUsers/' + country + '/Mine', loaderConfig)
  }

  getUserCountryData(countryId: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    return this._httpService.get<any>(environment.apiUrl + 'countries/' + countryId, loaderConfig, undefined);
  }

  editCurrentUser(country: string, body: UserProfileEdit, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<UserProfile>> {
    return this._httpService.put(environment.apiUrl + 'ClientPortalUsers/' + country + '/Mine', body, undefined, loaderConfig)
  }

  editCurrentUserAvatar(country: string, body: FormData, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
    return this._httpService.patch(environment.apiUrl + 'ClientPortalUsers/' + country + '/Mine/Avatar', body, undefined, loaderConfig)
  }
  
}import { getDaysInMonth, monthsInQuarter } from 'date-fns';

